import { useMemo } from 'react';
import useSWR from 'swr';
import type { RequestFailure } from '@ff-it/api';
import type { CurrencyRate } from 'modules/invoicing/common/types';
import { useExpenseVATOptions } from 'modules/core/vat';
import { formatError } from 'utilities';
import { asCredit } from './asCredit';
import type { ExtractResponse } from './types';

const EMPTY: ExtractResponse['matched_partners'] = [];

export function useExtract(attachmentId: number): {
  extracted: ExtractResponse['extracted'] | null;
  failure: string | null;
  isLoading: boolean;
  vatRate: number | null;
  rateUnknown: boolean;
  altCurrency: CurrencyRate | null;
  initialValues: Record<string, unknown>;
  matchedPartners: ExtractResponse['matched_partners'];
} {
  const vatRates = useExpenseVATOptions().map((o) => o.value);
  const {
    data: extractResponse,
    isLoading,
    error,
  } = useSWR<ExtractResponse, RequestFailure<{ detail?: string }>>(
    { url: 'invoice-capture/extract/', method: 'POST', body: { attachment: attachmentId } },
    {
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );

  const failure = error ? formatError(error) || `Extraction request failed (${error.status})` : null;
  const extracted = extractResponse?.extracted ?? null;
  const matchedPartners = extractResponse?.matched_partners ?? EMPTY;

  const vatRate = extracted?.vat_rate == null ? null : Math.abs(extracted.vat_rate);
  const vatAmount = Math.abs(Number(extracted?.vat_amount ?? 0));
  const rateIsBookable = vatRate != null && vatRates.includes(vatRate);
  const rateUnknown = !!extracted && !rateIsBookable && vatAmount > 0;

  const currency = extracted?.currency ?? null;
  const isForeign = !!currency && currency !== 'EUR';

  const { data: rates, isLoading: ratesLoading } = useSWR<{ results: CurrencyRate[] }>(
    isForeign && extracted?.invoice_date
      ? { url: 'core/rates/', method: 'GET', queryParams: { date: extracted.invoice_date } }
      : null,
  );
  const altCurrency = rates?.results.find((r) => r.currency === currency) ?? null;

  const initialValues = useMemo<Record<string, unknown>>(() => {
    if (!extracted) {
      return {};
    }
    const vat_amount = extracted.is_credit_note ? asCredit(extracted.vat_amount) : (extracted.vat_amount ?? undefined);
    const total = extracted.is_credit_note ? asCredit(extracted.total) : (extracted.total ?? undefined);
    return {
      type: extracted.is_credit_note ? 'CREDIT' : 'INVOICE',
      number: extracted.invoice_number ?? undefined,
      partner: matchedPartners.length === 1 ? matchedPartners[0] : undefined,
      checkout_date: extracted.invoice_date ?? undefined,
      payment_date: extracted.payment_date ?? undefined,
      vat_rate: rateIsBookable ? vatRate : rateUnknown ? undefined : 0,
      alt_currency: altCurrency ?? undefined,
      vat_amount: isForeign ? undefined : vat_amount,
      total: isForeign ? undefined : total,
      alt_vat_amount: isForeign ? vat_amount : undefined,
      alt_total: isForeign ? total : undefined,
    };
  }, [extracted, matchedPartners, vatRate, rateIsBookable, rateUnknown, altCurrency, isForeign]);

  return {
    extracted,
    failure,
    isLoading: isLoading || ratesLoading,
    vatRate,
    rateUnknown,
    altCurrency,
    initialValues,
    matchedPartners,
  };
}
